import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Users, ChefHat, Star, Printer, Share2, ArrowLeft, UtensilsCrossed, ExternalLink, Minus, Plus, ShoppingBasket, Check, PlayCircle, Youtube, ImageIcon, Video } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  scaleIngredients,
  addRecipeToShoppingList,
  loadShoppingList,
} from "@/lib/recipeMath";


// ------------------------------------------------------------------
// RecipeVideoSection — renders the primary YouTube video embed plus
// secondary video links, YouTube search fallback, image search, and
// any external website resources. Fully data-driven; renders nothing
// if no video data exists on the recipe.
// ------------------------------------------------------------------
function extractYouTubeId(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1) || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const m = u.pathname.match(/^\/(embed|shorts|v)\/([^/?#]+)/);
      if (m) return m[2];
    }
  } catch { /* ignore parse errors — fall through */ }
  return null;
}

function RecipeVideoSection({ recipe }) {
  const primaryId = extractYouTubeId(recipe.video_url);
  // Secondary videos = all `video_urls` that aren't the primary or a search URL,
  // deduplicated, capped at 4 for a clean row.
  const secondary = (recipe.video_urls || [])
    .filter((u) => typeof u === "string" && u !== recipe.video_url && !u.includes("/results?search_query"))
    .filter((u, i, arr) => arr.indexOf(u) === i)
    .slice(0, 4);
  const searchQuery = recipe.video_search_url ||
    `https://www.youtube.com/results?search_query=${encodeURIComponent((recipe.title || "") + " recipe")}`;
  const imageSearch = recipe.image_search_url ||
    `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(recipe.title || "")}`;
  return (
    <section
      className="mb-8 p-5 rounded-lg border"
      style={{ borderColor: "var(--wofy-line)", background: "#FDFBF7" }}
      data-testid="recipe-video-section"
    >
      <div className="flex items-center gap-2 mb-3">
        <Video size={18} style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-2xl">Watch &amp; learn</h2>
      </div>

      {primaryId ? (
        <div className="aspect-video w-full mb-4 rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-line)" }}>
          <iframe
            src={`https://www.youtube.com/embed/${primaryId}?rel=0`}
            title={`${recipe.title} — video`}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="recipe-video-primary"
          />
        </div>
      ) : (
        // Curated video isn't available for this recipe — send users straight
        // to a YouTube search seeded with the recipe title.
        <a
          href={searchQuery}
          target="_blank"
          rel="noopener noreferrer"
          className="block aspect-video w-full mb-4 rounded-lg border overflow-hidden group relative"
          style={{
            borderColor: "var(--wofy-line)",
            background: `linear-gradient(135deg, rgba(196,127,58,0.14), rgba(92,107,82,0.12))`,
          }}
          data-testid="recipe-video-search-card"
        >
          {recipe.cover_photo && (
            <img
              src={recipe.cover_photo}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="absolute inset-0 grid place-items-center text-center px-4">
            <div>
              <PlayCircle size={56} className="mx-auto mb-2 text-white drop-shadow-lg" />
              <div className="font-display text-xl text-white drop-shadow-lg mb-1">Search video tutorials on YouTube</div>
              <div className="text-xs uppercase tracking-widest text-white/90 inline-flex items-center gap-1 mt-1">
                <Youtube size={12} /> Fresh results for &quot;{recipe.title}&quot;
              </div>
            </div>
          </div>
        </a>
      )}

      {secondary.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2 opacity-70">
            More video guides
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="recipe-video-secondary">
            {secondary.map((url, i) => {
              const id = extractYouTubeId(url);
              const thumb = id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
              return (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block aspect-video rounded overflow-hidden border hover:shadow-md transition-shadow"
                  style={{ borderColor: "var(--wofy-line)", background: "#0002" }}
                  data-testid={`recipe-video-secondary-${i}`}
                >
                  {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-xs opacity-60">Watch →</div>
                  )}
                  <div className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/50 transition-colors">
                    <PlayCircle size={28} className="text-white/95" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <a
          href={searchQuery}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border hover:bg-black/[0.03]"
          style={{ borderColor: "var(--wofy-line)", color: "#c4302b" }}
          data-testid="recipe-video-search"
        >
          <Youtube size={13} /> More on YouTube
        </a>
        <a
          href={imageSearch}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border hover:bg-black/[0.03]"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="recipe-image-search"
        >
          <ImageIcon size={13} /> More photos
        </a>
        {(recipe.website_urls || []).slice(0, 5).map((url, i) => {
          let host = "link"; try { host = new URL(url).hostname.replace(/^www\./, ""); } catch {}
          return (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border hover:bg-black/[0.03]"
              style={{ borderColor: "var(--wofy-line)" }}
              data-testid={`recipe-website-${i}`}
            >
              <ExternalLink size={13} /> {host}
            </a>
          );
        })}
      </div>
    </section>
  );
}

function StarPicker({ current = 0, onPick }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="inline-flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          data-testid={`recipe-rate-${i}`}
          onClick={() => onPick(i)}
          onMouseEnter={() => setHover(i)}
          className="p-0.5"
          aria-label={`Rate ${i} stars`}
        >
          <Star size={22} style={{ color: i <= (hover || current) ? "#c47f3a" : "#c8c1af", fill: i <= (hover || current) ? "#c47f3a" : "transparent" }} />
        </button>
      ))}
    </div>
  );
}

export default function RecipeDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRating, setMyRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [posting, setPosting] = useState(false);
  const [servings, setServings] = useState(null); // per-user override, defaults to recipe.servings
  const [inCart, setInCart] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/recipes/${slug}`)
      .then((r) => { setRecipe(r.data.recipe); setRelated(r.data.related || []); setReviews(r.data.reviews || []); })
      .catch(() => setRecipe(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  // Initialize servings override + shopping-cart membership once the recipe loads.
  useEffect(() => {
    if (recipe && servings == null) setServings(recipe.servings || 4);
    if (recipe) {
      const list = loadShoppingList();
      setInCart(!!(list.recipes || {})[recipe.slug]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe]);

  const effectiveServings = servings || (recipe && recipe.servings) || 4;
  const base = (recipe && recipe.servings) || 4;
  const scaleFactor = effectiveServings / base;
  const scaledIngredients = useMemo(
    () => (recipe ? scaleIngredients(recipe.ingredients || [], scaleFactor) : []),
    [recipe, scaleFactor],
  );

  const addToCart = () => {
    if (!recipe) return;
    addRecipeToShoppingList(recipe, effectiveServings);
    setInCart(true);
    toast.success("Added to shopping list");
  };

  if (loading) return <div data-testid="recipe-loading" className="max-w-4xl mx-auto px-6 py-16" style={{ color: "var(--wofy-text-soft)" }}>Loading recipe…</div>;
  if (!recipe) return (
    <div data-testid="recipe-missing" className="max-w-4xl mx-auto px-6 py-16">
      <h1 className="font-display text-3xl mb-4">Recipe not found.</h1>
      <Link to="/recipes" className="underline" style={{ color: "var(--wofy-primary)" }}>Browse all recipes</Link>
    </div>
  );

  const rate = async (stars) => {
    if (!user) { toast.error("Please sign in to rate this recipe"); return; }
    try {
      const { data } = await http.post(`/recipes/${recipe.id}/rate`, { stars });
      setMyRating(stars);
      setRecipe({ ...recipe, rating_avg: data.rating_avg, rating_count: data.rating_count });
      toast.success(`Thanks! Rated ${stars} ★`);
    } catch { toast.error("Could not save rating"); }
  };

  const submitReview = async () => {
    if (!user) { toast.error("Please sign in to review this recipe"); return; }
    if (reviewText.trim().length < 5) return toast.error("Please write a longer review");
    setPosting(true);
    try {
      const { data } = await http.post(`/recipes/${recipe.id}/reviews`, { body: reviewText.trim() });
      setReviews([data, ...reviews]);
      setReviewText("");
      toast.success("Thanks for your review");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post review"); }
    finally { setPosting(false); }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: recipe.title, url }); } catch {} }
    else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  const totalMin = recipe.total_time_min || (recipe.prep_time_min + recipe.cook_time_min);

  return (
    <div data-testid="recipe-detail" className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/recipes" className="inline-flex items-center gap-1 text-sm underline mb-6" style={{ color: "var(--wofy-primary)" }}>
        <ArrowLeft size={14} /> All recipes
      </Link>

      <div className="overline mb-2 flex items-center gap-2 flex-wrap" style={{ color: "var(--wofy-primary)" }}>
        <span>{recipe.cuisine}</span><span>·</span>
        <span>{recipe.country}{recipe.region ? ` · ${recipe.region}` : ""}</span><span>·</span>
        <span>{recipe.category}</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mb-2">{recipe.title}</h1>
      <p className="text-lg mb-4" style={{ color: "var(--wofy-text-soft)" }}>{recipe.short_description}</p>

      <div className="flex items-center gap-2 mb-6 flex-wrap text-sm">
        <span className="inline-flex items-center gap-1"><Clock size={14} /> {totalMin} min total (prep {recipe.prep_time_min} · cook {recipe.cook_time_min})</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Users size={14} /> Serves {recipe.servings}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><ChefHat size={14} /> {recipe.difficulty}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Star size={14} style={{ color: "#c47f3a", fill: "#c47f3a" }} />
          {recipe.rating_count ? `${recipe.rating_avg.toFixed(1)} (${recipe.rating_count})` : "New"}
        </span>
      </div>

      {recipe.cover_photo && (
        <img
          src={recipe.cover_photo} alt={recipe.title}
          className="w-full max-h-[520px] object-cover rounded-lg mb-6"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}

      {recipe.ai_summary && (
        <div className="mb-6 p-4 rounded-lg border italic" style={{ borderColor: "var(--wofy-line)", background: "rgba(196,127,58,0.06)" }}>
          {recipe.ai_summary}
        </div>
      )}

      <RecipeVideoSection recipe={recipe} />

      <div className="flex gap-2 mb-8 flex-wrap">
        <Link
          data-testid="recipe-print-btn"
          to={`/recipes/${recipe.slug}/print`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold"
          style={{ borderColor: "var(--wofy-line)" }}
        >
          <Printer size={13} /> Printable card
        </Link>
        <button data-testid="recipe-share-btn" onClick={share} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--wofy-line)" }}>
          <Share2 size={13} /> Share
        </button>
        {recipe.website_url && (
          <a href={recipe.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--wofy-line)" }}>
            <ExternalLink size={13} /> Original source
          </a>
        )}
      </div>

      {/* Ingredients + Steps */}
      <div className="grid md:grid-cols-[280px_1fr] gap-8 mb-10">
        <aside>
          <h2 className="font-display text-2xl mb-3">Ingredients</h2>

          {/* Portions scaler */}
          <div className="mb-3 flex items-center gap-2 text-xs" data-testid="portion-scaler" style={{ color: "var(--wofy-text-soft)" }}>
            <span>Serves</span>
            <button
              type="button"
              onClick={() => setServings(Math.max(1, effectiveServings - 1))}
              data-testid="portion-minus"
              aria-label="Decrease servings"
              className="w-7 h-7 rounded-full grid place-items-center border"
              style={{ borderColor: "var(--wofy-line)" }}
              disabled={effectiveServings <= 1}
            >
              <Minus size={12} />
            </button>
            <input
              type="number"
              min={1}
              max={200}
              value={effectiveServings}
              onChange={(e) => setServings(Math.max(1, Math.min(200, parseInt(e.target.value || "1", 10))))}
              data-testid="portion-input"
              className="w-14 px-2 py-1 rounded border text-center font-semibold"
              style={{ borderColor: "var(--wofy-line)", background: "transparent" }}
            />
            <button
              type="button"
              onClick={() => setServings(Math.min(200, effectiveServings + 1))}
              data-testid="portion-plus"
              aria-label="Increase servings"
              className="w-7 h-7 rounded-full grid place-items-center border"
              style={{ borderColor: "var(--wofy-line)" }}
            >
              <Plus size={12} />
            </button>
            {effectiveServings !== base && (
              <button
                type="button"
                onClick={() => setServings(base)}
                data-testid="portion-reset"
                className="ml-1 underline opacity-70 hover:opacity-100"
              >
                reset
              </button>
            )}
          </div>

          <ul className="space-y-2 text-sm">
            {scaledIngredients.map((i, idx) => (
              <li key={idx} className="flex gap-2 border-b pb-2" style={{ borderColor: "var(--wofy-line)" }}>
                <span className="font-semibold whitespace-nowrap">{[i.quantity, i.unit].filter(Boolean).join(" ")}</span>
                <span>{i.name}{i.optional ? <em className="text-xs opacity-70"> (optional)</em> : ""}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={addToCart}
            data-testid="add-to-shopping"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-semibold"
            style={{
              background: inCart ? "var(--wofy-surface)" : "var(--wofy-primary)",
              color: inCart ? "var(--wofy-text)" : "#FDFBF7",
              border: `1px solid ${inCart ? "var(--wofy-primary)" : "transparent"}`,
            }}
          >
            {inCart ? <Check size={14} /> : <ShoppingBasket size={14} />}
            {inCart ? "Update in shopping list" : "Add to shopping list"}
          </button>
          <Link
            to="/recipes/shopping-list"
            data-testid="open-shopping-list"
            className="mt-2 block text-center text-xs underline opacity-70 hover:opacity-100"
          >
            View shopping list ↗
          </Link>
        </aside>
        <div>
          <h2 className="font-display text-2xl mb-3">Method</h2>
          <ol className="space-y-4">
            {(recipe.steps || []).map((s, i) => (
              <li key={i} className="grid grid-cols-[28px_1fr] gap-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "var(--wofy-primary)", color: "white" }}>
                  {s.n || i + 1}
                </span>
                <div>
                  <div className="leading-relaxed">{s.text}</div>
                  {s.tip && <div className="text-xs italic mt-1" style={{ color: "var(--wofy-text-soft)" }}>Tip: {s.tip}</div>}
                </div>
              </li>
            ))}
          </ol>
          {recipe.cooking_tips && recipe.cooking_tips.length > 0 && (
            <div className="mt-6 p-4 border rounded-lg" style={{ borderColor: "var(--wofy-line)", background: "rgba(92,107,82,0.06)" }}>
              <div className="font-semibold text-sm mb-2">Chef's tips</div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {recipe.cooking_tips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      {recipe.story && (
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-3">The story</h2>
          <p className="leading-relaxed whitespace-pre-line" style={{ color: "var(--wofy-text)" }}>{recipe.story}</p>
        </section>
      )}

      {/* Rating + reviews */}
      <section className="mb-10 border-t pt-8" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <h2 className="font-display text-2xl">Rate this recipe</h2>
          <StarPicker current={myRating} onPick={rate} />
          {recipe.rating_count > 0 && (
            <span className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Community: {recipe.rating_avg.toFixed(1)} avg from {recipe.rating_count} raters
            </span>
          )}
        </div>

        <h3 className="font-display text-xl mt-6 mb-3">Reviews ({reviews.length})</h3>
        {user ? (
          <div className="mb-6 flex flex-col gap-2">
            <textarea
              data-testid="recipe-review-input"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={3}
              placeholder="Share your notes, substitutions, or how it turned out…"
              className="border rounded p-2 text-sm"
              style={{ borderColor: "var(--wofy-line)" }}
            />
            <button data-testid="recipe-review-submit" onClick={submitReview} disabled={posting} className="self-end px-4 py-2 rounded-full font-semibold text-sm" style={{ background: "var(--wofy-primary)", color: "white" }}>
              {posting ? "Posting…" : "Post review"}
            </button>
          </div>
        ) : (
          <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
            <Link className="underline" to="/login" style={{ color: "var(--wofy-primary)" }}>Sign in</Link> to leave a review or rating.
          </p>
        )}

        {reviews.length === 0 ? (
          <div className="text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>No reviews yet. Be the first to try it and share your notes.</div>
        ) : (
          <ul className="space-y-4">
            {reviews.map((rv) => (
              <li key={rv.id} className="border-b pb-3" style={{ borderColor: "var(--wofy-line)" }}>
                <div className="font-semibold text-sm mb-1">{rv.user_name} <span className="opacity-70 text-xs font-normal">· {new Date(rv.created_at).toLocaleDateString()}</span></div>
                <div className="text-sm whitespace-pre-line">{rv.body}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section>
          <h2 className="font-display text-2xl mb-4">You might also try</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {related.map((r) => (
              <Link key={r.id} to={`/recipes/${r.slug}`} data-testid={`recipe-related-${r.id}`} className="block rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-line)" }}>
                <div className="w-full aspect-square flex items-center justify-center" style={{ background: "var(--wofy-card-bg,#f2ede4)" }}>
                  {r.cover_photo ? (
                    <img src={r.cover_photo} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <UtensilsCrossed size={24} style={{ color: "var(--wofy-primary)" }} />
                  )}
                </div>
                <div className="p-2">
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>{r.cuisine}</div>
                  <div className="text-xs leading-snug">{r.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
